import { execFile } from "node:child_process";
import { randomUUID } from "node:crypto";
import { constants as fsConstants } from "node:fs";
import fs from "node:fs/promises";
import os from "node:os";
import path from "node:path";
import { promisify } from "node:util";
import type { NoriConfig } from "@mewbleh/nori-core";
import type { NoriPaths } from "@mewbleh/nori-persistence";
import { backup as sqliteBackup, DatabaseSync } from "node:sqlite";

const execFileAsync = promisify(execFile);
const ARCHIVE_ROOT = "nori-backup";

type BackupSource = { source: string; archivePath: string; kind: "state" | "config" | "workspace" };
export type BackupManifest = {
  format: "nori-backup-v1";
  noriVersion: string;
  createdAt: string;
  sources: BackupSource[];
  skippedVolatileCount: number;
};

function inside(candidate: string, parent: string): boolean {
  const relative = path.relative(parent, candidate);
  return relative === "" || (!relative.startsWith("..") && !path.isAbsolute(relative));
}

async function exists(target: string): Promise<boolean> {
  try { await fs.access(target); return true; }
  catch { return false; }
}

async function canonical(target: string): Promise<string> {
  try { return await fs.realpath(target); }
  catch { return path.resolve(target); }
}

async function planSources(paths: NoriPaths, config: NoriConfig, includeWorkspace: boolean, onlyConfig: boolean): Promise<BackupSource[]> {
  const candidates: BackupSource[] = onlyConfig
    ? [{ source: paths.configPath, archivePath: "payload/config/nori.json", kind: "config" }]
    : [
        { source: paths.stateDir, archivePath: "payload/state", kind: "state" },
        { source: paths.configPath, archivePath: "payload/config/nori.json", kind: "config" },
        ...(includeWorkspace
          ? config.agents.list.map((agent) => ({
              source: agent.workspace ?? config.agents.defaults.workspace,
              archivePath: `payload/workspaces/${agent.id}`,
              kind: "workspace" as const,
            }))
          : []),
      ];
  const resolved: BackupSource[] = [];
  for (const candidate of candidates) {
    if (!(await exists(candidate.source))) continue;
    const source = await canonical(candidate.source);
    if (resolved.some((entry) => inside(source, entry.source))) continue;
    resolved.push({ ...candidate, source });
  }
  return resolved;
}

function volatile(relative: string): boolean {
  const normalized = relative.split(path.sep).join("/");
  const name = path.posix.basename(normalized);
  return normalized === "logs" || normalized.startsWith("logs/")
    || name === "gateway.lock"
    || name.endsWith(".sqlite-wal")
    || name.endsWith(".sqlite-shm")
    || name.endsWith(".pid")
    || name.endsWith(".sock")
    || name.endsWith(".tmp");
}

async function copySnapshot(source: string, destination: string, relative = "", counters = { skipped: 0 }): Promise<void> {
  const stat = await fs.lstat(source);
  if (relative && volatile(relative)) {
    counters.skipped += 1;
    return;
  }
  if (stat.isSymbolicLink()) {
    counters.skipped += 1;
    return;
  }
  if (stat.isDirectory()) {
    await fs.mkdir(destination, { recursive: true, mode: 0o700 });
    for (const entry of await fs.readdir(source, { withFileTypes: true })) {
      await copySnapshot(path.join(source, entry.name), path.join(destination, entry.name), path.join(relative, entry.name), counters);
    }
    return;
  }
  if (!stat.isFile()) {
    counters.skipped += 1;
    return;
  }
  await fs.mkdir(path.dirname(destination), { recursive: true, mode: 0o700 });
  if (source.endsWith(".sqlite")) {
    const database = new DatabaseSync(source, { readOnly: true });
    try { await sqliteBackup(database, destination); }
    finally { database.close(); }
    if (process.platform !== "win32") await fs.chmod(destination, 0o600);
    return;
  }
  await fs.copyFile(source, destination);
  if (process.platform !== "win32") await fs.chmod(destination, stat.mode & 0o777);
}

function archiveName(): string {
  return `${new Date().toISOString().replaceAll(":", "-").replace(".", "-")}-nori-backup.tar.gz`;
}

async function resolveOutput(value: string | undefined): Promise<string> {
  if (!value) return path.join(process.cwd(), archiveName());
  const output = path.resolve(value.replace(/^~(?=$|\/)/u, os.homedir()));
  try {
    return (await fs.stat(output)).isDirectory() ? path.join(output, archiveName()) : output;
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code !== "ENOENT") throw error;
    return output.endsWith(".tar.gz") || output.endsWith(".tgz") ? output : path.join(output, archiveName());
  }
}

export async function createBackup(
  paths: NoriPaths,
  config: NoriConfig,
  options: { output?: string; dryRun?: boolean; verify?: boolean; includeWorkspace?: boolean; onlyConfig?: boolean; version?: string } = {},
): Promise<Record<string, unknown>> {
  const sources = await planSources(paths, config, options.includeWorkspace !== false, options.onlyConfig === true);
  if (!sources.length) throw new Error("No existing Nori paths are available to back up");
  let outputPath = await resolveOutput(options.output);
  if (!options.output && sources.some((source) => inside(outputPath, source.source))) outputPath = path.join(os.homedir(), archiveName());
  if (sources.some((source) => inside(outputPath, source.source))) throw new Error("Backup output cannot be inside a backed-up source tree");
  if (await exists(outputPath)) throw new Error(`Backup output already exists: ${outputPath}`);
  if (options.dryRun) return { dryRun: true, outputPath, sources, includeWorkspace: options.includeWorkspace !== false, onlyConfig: options.onlyConfig === true };

  await fs.mkdir(path.dirname(outputPath), { recursive: true, mode: 0o700 });
  const staging = await fs.mkdtemp(path.join(os.tmpdir(), "nori-backup-"));
  const temporaryArchive = path.join(path.dirname(outputPath), `.${path.basename(outputPath)}.${process.pid}.tmp`);
  const counters = { skipped: 0 };
  try {
    const root = path.join(staging, ARCHIVE_ROOT);
    await fs.mkdir(root, { recursive: true, mode: 0o700 });
    for (const source of sources) await copySnapshot(source.source, path.join(root, source.archivePath), "", counters);
    const manifest: BackupManifest = {
      format: "nori-backup-v1",
      noriVersion: options.version ?? "0.3.0",
      createdAt: new Date().toISOString(),
      sources,
      skippedVolatileCount: counters.skipped,
    };
    await fs.writeFile(path.join(root, "manifest.json"), `${JSON.stringify(manifest, null, 2)}\n`, { mode: 0o600 });
    await execFileAsync("tar", ["-czf", temporaryArchive, "-C", staging, ARCHIVE_ROOT], { timeout: 30 * 60_000, maxBuffer: 16 * 1024 * 1024 });
    try {
      await fs.link(temporaryArchive, outputPath);
    } catch (error) {
      if (!["EXDEV", "EPERM", "EACCES", "ENOTSUP"].includes((error as NodeJS.ErrnoException).code ?? "")) throw error;
      await fs.copyFile(temporaryArchive, outputPath, fsConstants.COPYFILE_EXCL);
    }
    if (process.platform !== "win32") await fs.chmod(outputPath, 0o600);
    const verification = options.verify ? await verifyBackup(outputPath) : undefined;
    return { ok: true, outputPath, sources, skippedVolatileCount: counters.skipped, ...(verification ? { verification } : {}) };
  } finally {
    await fs.rm(temporaryArchive, { force: true });
    await fs.rm(staging, { recursive: true, force: true });
  }
}

export async function verifyBackup(archivePath: string): Promise<{ ok: true; archivePath: string; entries: number; manifest: BackupManifest }> {
  const resolved = path.resolve(archivePath);
  const { stdout: listing } = await execFileAsync("tar", ["-tzf", resolved], { timeout: 30 * 60_000, maxBuffer: 64 * 1024 * 1024 });
  const entries = listing.split(/\r?\n/u).filter(Boolean);
  if (entries.length > 1_000_000) throw new Error("Backup contains too many archive entries");
  const seen = new Set<string>();
  for (const entry of entries) {
    const normalized = entry.replace(/\/$/u, "");
    if (path.posix.isAbsolute(normalized) || normalized.split("/").includes("..") || normalized.includes("\\")) throw new Error(`Unsafe archive path: ${entry}`);
    if (seen.has(normalized)) throw new Error(`Duplicate archive path: ${entry}`);
    seen.add(normalized);
  }
  const { stdout: verbose } = await execFileAsync("tar", ["-tvzf", resolved, "--numeric-owner"], { timeout: 30 * 60_000, maxBuffer: 64 * 1024 * 1024, env: { ...process.env, LC_ALL: "C" } });
  for (const line of verbose.split(/\r?\n/u).filter(Boolean)) {
    if (line[0] !== "d" && line[0] !== "-") throw new Error(`Backup contains an unsafe special archive entry: ${line}`);
  }
  const manifestEntries = entries.filter((entry) => entry.replace(/\/$/u, "") === `${ARCHIVE_ROOT}/manifest.json`);
  if (manifestEntries.length !== 1) throw new Error("Backup must contain exactly one root manifest.json");
  const { stdout } = await execFileAsync("tar", ["-xOzf", resolved, `${ARCHIVE_ROOT}/manifest.json`], { timeout: 30_000, maxBuffer: 4 * 1024 * 1024 });
  const manifest = JSON.parse(stdout) as BackupManifest;
  if (manifest.format !== "nori-backup-v1" || !Array.isArray(manifest.sources)) throw new Error("Unsupported or malformed Nori backup manifest");
  for (const source of manifest.sources) {
    const expected = `${ARCHIVE_ROOT}/${source.archivePath}`;
    if (!entries.some((entry) => entry.replace(/\/$/u, "") === expected || entry.startsWith(`${expected}/`))) throw new Error(`Manifest payload is missing: ${source.archivePath}`);
  }
  return { ok: true, archivePath: resolved, entries: entries.length, manifest };
}

async function copyDirectoryContents(source: string, destination: string): Promise<void> {
  for (const entry of await fs.readdir(source, { withFileTypes: true })) {
    await fs.cp(path.join(source, entry.name), path.join(destination, entry.name), {
      recursive: true,
      errorOnExist: true,
      force: false,
      preserveTimestamps: true,
    });
  }
}

async function inspectExtractedTree(root: string): Promise<{ entries: number; bytes: number }> {
  let entries = 0;
  let bytes = 0;
  const walk = async (directory: string): Promise<void> => {
    for (const entry of await fs.readdir(directory, { withFileTypes: true })) {
      entries += 1;
      if (entries > 1_000_000) throw new Error("Extracted backup contains too many entries");
      const fullPath = path.join(directory, entry.name);
      const stat = await fs.lstat(fullPath);
      if (stat.isSymbolicLink() || (!stat.isDirectory() && !stat.isFile())) throw new Error(`Extracted backup contains an unsafe entry: ${fullPath}`);
      if (stat.isDirectory()) await walk(fullPath);
      else bytes += stat.size;
    }
  };
  await walk(root);
  return { entries, bytes };
}

async function gatewayIsRunning(target: string): Promise<boolean> {
  try {
    const pid = Number.parseInt(await fs.readFile(path.join(target, "gateway.lock"), "utf8"), 10);
    if (!Number.isInteger(pid) || pid < 1) return false;
    process.kill(pid, 0);
    return true;
  } catch (error) {
    return !["ENOENT", "ESRCH"].includes((error as NodeJS.ErrnoException).code ?? "");
  }
}

export async function restoreBackup(
  paths: NoriPaths,
  archivePath: string,
  options: { target?: string; apply?: boolean; force?: boolean } = {},
): Promise<Record<string, unknown>> {
  const verified = await verifyBackup(archivePath);
  const target = path.resolve(options.target?.replace(/^~(?=$|\/)/u, os.homedir()) ?? paths.stateDir);
  if (target === path.parse(target).root) throw new Error("Refusing to restore a backup over a filesystem root");
  const targetStat = await fs.stat(target).catch((error: NodeJS.ErrnoException) => error.code === "ENOENT" ? undefined : Promise.reject(error));
  if (targetStat && !targetStat.isDirectory()) throw new Error("Backup restore target must be a directory");
  const targetEntries = targetStat ? await fs.readdir(target) : [];
  const requiresForce = targetEntries.length > 0;
  const mappings = verified.manifest.sources.map((source) => ({ archivePath: source.archivePath, kind: source.kind }));
  if (!options.apply) return { dryRun: true, archivePath: verified.archivePath, target, requiresForce, mappings };
  if (requiresForce && !options.force) throw new Error("Backup restore target is not empty; rerun with --force to preserve and replace it");
  if (await gatewayIsRunning(target)) throw new Error("Stop the Nori Gateway before restoring its state");

  const extraction = await fs.mkdtemp(path.join(os.tmpdir(), "nori-restore-extract-"));
  await fs.mkdir(path.dirname(target), { recursive: true, mode: 0o700 });
  const transaction = await fs.mkdtemp(path.join(path.dirname(target), ".nori-restore-"));
  const candidate = path.join(transaction, "state");
  const displacedPath = `${target}.pre-restore-${new Date().toISOString().replaceAll(":", "-")}-${randomUUID().slice(0, 8)}`;
  let displaced = false;
  try {
    await execFileAsync("tar", ["-xzf", verified.archivePath, "-C", extraction, "--no-same-owner", "--no-same-permissions"], {
      timeout: 30 * 60_000,
      maxBuffer: 16 * 1024 * 1024,
      env: { ...process.env, LC_ALL: "C" },
    });
    const extractedRoot = path.join(extraction, ARCHIVE_ROOT);
    const inspected = await inspectExtractedTree(extractedRoot);
    await fs.mkdir(candidate, { recursive: true, mode: 0o700 });
    const statePayload = path.join(extractedRoot, "payload", "state");
    const configPayload = path.join(extractedRoot, "payload", "config", "nori.json");
    const workspacePayload = path.join(extractedRoot, "payload", "workspaces");
    let restoredPayloads = 0;
    if (await exists(statePayload)) { await copyDirectoryContents(statePayload, candidate); restoredPayloads += 1; }
    if (await exists(configPayload)) {
      await fs.copyFile(configPayload, path.join(candidate, "nori.json"), fsConstants.COPYFILE_EXCL);
      restoredPayloads += 1;
    }
    if (await exists(workspacePayload)) {
      await fs.cp(workspacePayload, path.join(candidate, "restored-workspaces"), { recursive: true, errorOnExist: true, force: false, preserveTimestamps: true });
      restoredPayloads += 1;
    }
    if (!restoredPayloads) throw new Error("Backup contains no restorable state, config, or workspace payload");
    if (process.platform !== "win32") await fs.chmod(candidate, 0o700);

    if (targetStat && targetEntries.length === 0) await fs.rmdir(target);
    else if (targetStat) {
      await fs.rename(target, displacedPath);
      displaced = true;
    }
    try {
      await fs.rename(candidate, target);
    } catch (error) {
      if (displaced) await fs.rename(displacedPath, target).catch(() => undefined);
      throw error;
    }
    return {
      ok: true,
      archivePath: verified.archivePath,
      target,
      inspected,
      ...(displaced ? { displacedPath } : {}),
      note: "External workspace payloads are restored under restored-workspaces and are never written to manifest-supplied absolute paths.",
    };
  } finally {
    await fs.rm(extraction, { recursive: true, force: true });
    await fs.rm(transaction, { recursive: true, force: true });
  }
}
